import styled from "@emotion/styled";
import { MDSLoadingSpinner } from "@/mds/components/loading/loading-spinner";
import { LoadingContainer } from "@/components/note/editor/source-bar/floating-preview-content.styles";
import { NoteSourceApiRequestArtifactData } from "@/store/sources/types";
import { objectModule } from "@/modules/object";
import { isObject, orderBy, toString } from "lodash-es";
import { titleize } from "inflection";

/**
 * The order of these keys determines the order of the API request preview.
 * The keys are sorted by priority, and then alphabetically.
 *
 * See our docs for a full list of possible keys:
 * - https://docs.mem.ai/api-reference/notes/create-note
 */
const PRIORITY_KEYS = [
  "id",
  "timestamp",
  "created_at",
  "updated_at",
  "apply_template",
  "add_to_collections",
  "auto_format",
  "auto_organize",
  "instructions",
  "context",
  "input",
  "content",
];

type TProps = {
  isLoading: boolean;
  artifactData: NoteSourceApiRequestArtifactData | null;
};

export const FloatingApiRequestPreviewContent = ({ isLoading, artifactData }: TProps) => {
  const parsedPayload = artifactData?.payload ?? {};

  const cleanPayload = objectModule.compactNullOrUndefined({
    ...parsedPayload,
  });

  const keyValuePairs: [string, string][] = Object.entries(cleanPayload).map(([key, value]) => {
    const titleCaseKey = titleize(key);

    if (isObject(value) && value !== null) {
      return [titleCaseKey, JSON.stringify(value)];
    }

    return [titleCaseKey, toString(value)];
  });

  // Sort by priority keys first, then alphabetically
  const sortedKeyValuePairs = orderBy(keyValuePairs, [
    ([key]) => {
      const priorityIndex = PRIORITY_KEYS.indexOf(key);
      return priorityIndex === -1 ? Infinity : priorityIndex;
    },
    ([key]) => key,
  ]);

  return (
    <Content>
      {isLoading ? (
        <LoadingContainer>
          <MDSLoadingSpinner size={24} />
        </LoadingContainer>
      ) : (
        <ApiRequestPreviewGrid>
          {sortedKeyValuePairs.map(([key, value]) => (
            <>
              <ApiRequestPreviewKey>{key}</ApiRequestPreviewKey>
              <ApiRequestPreviewValue>{value}</ApiRequestPreviewValue>
            </>
          ))}
        </ApiRequestPreviewGrid>
      )}
    </Content>
  );
};

export const Content = styled.div`
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  flex-grow: 1;
  min-height: 0;
  width: min(500px, 90vw);
  height: min(400px, 80vh);
  max-width: 90vw;
  max-height: 80vh;
  padding: ${({ theme }) => theme.spacing["4"]};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
`;

const ApiRequestPreviewGrid = styled.div`
  margin: 0;
  padding: 0;
  display: grid;
  grid-template-columns: fit-content(120px) 1fr;
  gap: ${({ theme }) => theme.spacing["2"]};
  row-gap: ${({ theme }) => theme.spacing["4"]};
  width: 100%;
  border-radius: ${({ theme }) => theme.borderRadius.medium};
`;

const ApiRequestPreviewKey = styled.span`
  font-size: ${({ theme }) => theme.fontSizes["text-xs"]};
  line-height: ${({ theme }) => theme.lineHeights["text-s"]};
  font-weight: ${({ theme }) => theme.fontWeights.semiBold};
  overflow-wrap: break-word;
  word-break: break-word;
  hyphens: auto;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const ApiRequestPreviewValue = styled.span`
  font-size: ${({ theme }) => theme.fontSizes["text-s"]};
  color: ${({ theme }) => theme.colors.text.primary};
  word-break: break-word;
  overflow-wrap: break-word;
`;
